import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Droplets, Pill, RotateCcw, BedDouble, X, CheckCircle2, Cake } from "lucide-react";
import { useApp } from "../context/AppContext";
import { statusHidratacao, medicacaoNaHora, statusDecubito } from "../utils/alertas";
import { IMG_DECUBITO } from "../utils/decubito";
import DecubitoAlarm from "../components/DecubitoAlarm";

const agoraISO = () => {
  const d = new Date(); const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const PainelTV = () => {
  const nav = useNavigate();
  const { residentes, alertas, auth, addRegistroProntuario, refresh } = useApp();
  const [idx, setIdx] = useState(0);
  const [now, setNow] = useState(new Date());
  const [snooze, setSnooze] = useState({});
  const [, setTick] = useState(0);

  const stamp = ([auth?.nome, auth?.funcao].filter(Boolean).join(" — ")) +
    ((auth?.coren && auth?.perfil !== "cuidador") ? ` · ${auth.coren}` : "");

  useEffect(() => {
    const c = setInterval(() => setNow(new Date()), 1000);
    const t = setInterval(() => setTick((x) => x + 1), 20000);
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (e) { /* noop */ }
    }
    return () => { clearInterval(c); clearInterval(t); };
  }, []);

  const computados = useMemo(() => residentes
    .filter((r) => (r.status || "Ativo") === "Ativo")
    .map((r) => {
    const al = alertas?.[r.id] || {};
    const hid = statusHidratacao(al);
    const med = medicacaoNaHora(al);
    const decubito = statusDecubito(al);
    const pend = (decubito.ativo && decubito.due ? 3 : 0) + (hid.atrasada ? 2 : 0) + (med.naHora ? 1 : 0);
    return { r, hid, med, decubito, pend };
  }), [residentes, alertas]);

  const pendentes = computados.filter((x) => x.pend > 0).sort((a, b) => b.pend - a.pend);
  const exibir = pendentes.length > 0 ? pendentes : computados;
  const aniversariantes = useMemo(() => {
    const hoje = new Date();
    return computados.map((x) => x.r).filter((r) => {
      const d = r.dataNascimento ? new Date(`${String(r.dataNascimento).slice(0, 10)}T12:00:00`) : null;
      return d && !isNaN(d) && d.getDate() === hoje.getDate() && d.getMonth() === hoje.getMonth();
    }).map((r) => ({ r, idade: hoje.getFullYear() - new Date(`${String(r.dataNascimento).slice(0, 10)}T12:00:00`).getFullYear() }));
  }, [computados, now.getDate()]);

  // Rotação automática
  useEffect(() => {
    if (exibir.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % exibir.length), 6000);
    return () => clearInterval(id);
  }, [exibir.length]);
  const atualIdx = exibir.length ? idx % exibir.length : 0;
  const cur = exibir[atualIdx];

  // Alarme de decúbito (sirene) para o vencido mais urgente
  const decubitoDue = computados.filter((x) => x.decubito?.ativo && x.decubito?.due && (!snooze[x.r.id] || Date.now() > snooze[x.r.id]));
  const alarmItem = decubitoDue[0] ? { rid: decubitoDue[0].r.id, nome: decubitoDue[0].r.nome, quarto: decubitoDue[0].r.quarto, proxima: decubitoDue[0].decubito.proxima, texto: decubitoDue[0].decubito.texto } : null;
  const confirmarDecubito = async (rid, posicao) => {
    await addRegistroProntuario(rid, { tipo: "mudanca_decubito", data: agoraISO(), autor: stamp || "Equipe de Enfermagem", dados: { posicao } });
    setSnooze((sn) => { const n = { ...sn }; delete n[rid]; return n; });
    refresh();
  };
  const fecharDecubito = (rid) => setSnooze((sn) => ({ ...sn, [rid]: Date.now() + 15 * 60 * 1000 }));

  const iniciais = (nome) => (nome || "").split(" ").map((n) => n[0]).slice(0, 2).join("");
  const hhmm = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  if (!cur) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <p className="text-2xl text-slate-400">Nenhum residente cadastrado.</p>
      </div>
    );
  }

  const semPendencia = cur.pend === 0;

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid="painel-tv">
      {/* Topo */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center font-bold">SJ</div>
          <div>
            <p className="font-bold text-lg leading-tight">Casa de Repouso São Jorge</p>
            <p className="text-sm text-slate-400">Painel de Plantão · Monitor do Posto</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums" data-testid="tv-relogio">{hhmm}</p>
            <p className="text-sm text-slate-400">{pendentes.length} residente(s) em atenção</p>
          </div>
          <button onClick={() => nav("/residentes")} className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg px-4 py-2.5" data-testid="tv-sair">
            <X className="w-4 h-4" /> Sair do modo TV
          </button>
        </div>
      </div>

      {/* Aniversariantes do dia */}
      {aniversariantes.length > 0 && (
        <div className="mx-8 mt-4 rounded-2xl bg-gradient-to-r from-pink-600/40 via-fuchsia-600/30 to-amber-500/30 ring-2 ring-pink-400/60 px-6 py-4 flex items-center gap-5 flex-wrap" data-testid="tv-aniversariantes">
          <div className="flex items-center gap-3 text-pink-200">
            <Cake className="w-9 h-9 animate-bounce" />
            <span className="text-2xl font-bold">{aniversariantes.length === 1 ? "Aniversariante de hoje" : "Aniversariantes de hoje"}</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {aniversariantes.map(({ r, idade }) => (
              <div key={r.id} className="flex items-center gap-3 bg-slate-900/50 rounded-xl px-4 py-2" data-testid={`tv-aniversariante-${r.id}`}>
                {r.foto ? <img src={r.foto} alt={r.nome} className="w-12 h-12 rounded-lg object-cover" />
                  : <div className="w-12 h-12 rounded-lg bg-pink-600 flex items-center justify-center text-lg font-bold">{iniciais(r.nome)}</div>}
                <div>
                  <p className="text-xl font-semibold leading-tight">{r.nome}</p>
                  <p className="text-sm text-pink-100">{idade} anos · Quarto {r.quarto || "-"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Card central rotativo */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className={`w-full max-w-5xl rounded-3xl p-10 transition-all ${semPendencia ? "bg-slate-800" : "bg-gradient-to-br from-red-900/60 to-slate-800 ring-4 ring-red-500/50"}`} data-testid="tv-card">
          <div className="flex items-center gap-6">
            {cur.r.foto ? <img src={cur.r.foto} alt={cur.r.nome} className="w-28 h-28 rounded-2xl object-cover" />
              : <div className="w-28 h-28 rounded-2xl bg-blue-600 flex items-center justify-center text-4xl font-bold">{iniciais(cur.r.nome)}</div>}
            <div className="flex-1">
              <h1 className="text-5xl font-bold" data-testid="tv-residente">{cur.r.nome}</h1>
              <p className="text-2xl text-slate-300 mt-2 flex items-center gap-4">
                <span className="flex items-center gap-2"><BedDouble className="w-6 h-6" /> Quarto {cur.r.quarto || "-"}</span>
              </p>
            </div>
            {semPendencia && <span className="flex items-center gap-2 text-emerald-400 text-2xl font-semibold"><CheckCircle2 className="w-8 h-8" /> Tudo em dia</span>}
          </div>

          {/* Alertas grandes */}
          <div className="mt-8 space-y-4">
            {cur.decubito?.ativo && cur.decubito?.due && (
              <div className="flex items-center gap-5 bg-red-600 rounded-2xl p-5 animate-pulse">
                <img src={IMG_DECUBITO[cur.decubito.proxima]} alt={cur.decubito.proxima} className="w-24 h-24 rounded-xl object-contain bg-white" />
                <div>
                  <p className="text-3xl font-bold flex items-center gap-2"><RotateCcw className="w-8 h-8" /> MUDAR DECÚBITO</p>
                  <p className="text-2xl mt-1">Posição: {cur.decubito.proxima}</p>
                </div>
              </div>
            )}
            {cur.hid?.atrasada && (
              <div className="flex items-center gap-4 bg-sky-600 rounded-2xl p-5">
                <Droplets className="w-12 h-12" />
                <p className="text-3xl font-bold">HIDRATAR — {cur.hid.total} / 1600 mL hoje</p>
              </div>
            )}
            {cur.med?.naHora && (
              <div className="flex items-center gap-4 bg-amber-500 rounded-2xl p-5">
                <Pill className="w-12 h-12" />
                <p className="text-3xl font-bold">MEDICAÇÃO {cur.med.horario}</p>
              </div>
            )}
            {cur.decubito?.ativo && !cur.decubito?.due && (
              <p className="text-xl text-indigo-300 flex items-center gap-2"><RotateCcw className="w-6 h-6" /> Próxima mudança de decúbito às {cur.decubito.proximoHorario ? cur.decubito.proximoHorario.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "-"}</p>
            )}
          </div>

          {/* Indicadores de rotação */}
          {exibir.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {exibir.map((_, i) => (
                <span key={i} className={`h-2 rounded-full transition-all ${i === atualIdx ? "w-8 bg-white" : "w-2 bg-slate-600"}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      <DecubitoAlarm item={alarmItem} soundOn={!!alarmItem} onConfirm={confirmarDecubito} onSnooze={fecharDecubito} />
    </div>
  );
};

export default PainelTV;
